import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AnswerGroupTable, ColumnType } from '../components/AnswerGroupTable';
import type { NewReport } from '../hooks/useNewReport';
import { localizedText, type LocalizedText, type ReportTableConfiguration } from './types';
import { answeredRows, nonEmptyTextRows } from './tableRows';
import { comparisonConfig } from './tableComparisons';
import { useDefinitionMetrics } from './ReportDefinitionContext';

export function ReportTable({
  report,
  configuration,
  label,
}: {
  report: NewReport;
  configuration: ReportTableConfiguration;
  label: LocalizedText;
}) {
  const { t, i18n } = useTranslation('newReport');
  const { selectRows, filteredData, getAnswerGroup, selectComparisonGroups } = report;
  const metrics = useDefinitionMetrics();
  const rows = useMemo(() => {
    const roots =
      configuration.comparisonGroups === undefined
        ? filteredData ?? []
        : configuration.comparisonGroups.flatMap((group) => {
            if (group.type === 'baseline') return selectComparisonGroups(new Set([group.id]));
            const result = getAnswerGroup(comparisonConfig(group, i18n.language));
            return result ? [result] : [];
          });
    const mode =
      configuration.comparisonGroups && configuration.rowMode === 'participants'
        ? 'teamsAndParticipants'
        : configuration.rowMode;
    let groups = selectRows(roots, mode);
    if (configuration.responseQuestionIndex) groups = answeredRows(groups, configuration.responseQuestionIndex);
    const textColumn = configuration.columns.find((column) => column.id === configuration.nonEmptyTextColumnId);
    if (textColumn && (textColumn.type === ColumnType.TEXT || textColumn.type === ColumnType.THEMES))
      groups = nonEmptyTextRows(groups, textColumn.questionIndex, textColumn.statementIndex, textColumn.source);
    return groups;
  }, [selectRows, filteredData, getAnswerGroup, selectComparisonGroups, configuration, i18n.language]);
  const columns = useMemo(
    () =>
      configuration.columns.map((column) => {
        const metric = 'metricId' in column ? metrics.find((item) => item.id === column.metricId) : undefined;
        return {
          ...column,
          ...(metric && { format: metric.presentation.format }),
          label:
            localizedText(column.translations, i18n.language) ||
            ('metricId' in column
              ? localizedText(metric?.title, i18n.language)
              : column.label || t(`table.columns.${column.type}`)),
        };
      }),
    [configuration.columns, metrics, i18n.language, t],
  );
  return (
    <AnswerGroupTable
      pageSize={configuration.pageSize}
      sortingEnabled={configuration.sortingEnabled}
      searchEnabled={configuration.searchEnabled}
      columnOrderingEnabled={configuration.columnOrderingEnabled}
      columnResizeEnabled={configuration.columnResizeEnabled}
      horizontalScroll={configuration.horizontalScroll}
      columns={columns}
      groups={rows}
      label={localizedText(label, i18n.language)}
      resetKey={JSON.stringify([
        report.filters.answerSelections,
        [...report.filters.teamIds],
        configuration.rowMode,
        configuration.responseQuestionIndex,
        configuration.nonEmptyTextColumnId,
        configuration.comparisonGroups,
      ])}
    />
  );
}
